import { useState, useCallback } from 'react';
import { TopicNode, ErrorInfo } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { toast } from 'sonner'; // UIトースト通知用

/**
 * トピック抽出処理のためのHook
 * ノードとエッジの抽出処理とステータス管理を行う
 */
export function useExtraction() {
  const [extracting, setExtracting] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [currentlyProcessing, setCurrentlyProcessing] = useState<string | null>(null);

  /**
   * ノードの内容を抽出
   * @param node 抽出対象のノード
   * @param transcript トランスクリプト
   * @returns 更新されたノード
   */
  const extractNodeContent = useCallback(async (
    node: TopicNode,
    transcript: string
  ): Promise<TopicNode> => {
    try {
      setCurrentlyProcessing(node.id);
      
      // ノードのステータスを処理中に設定
      const processingNode = {
        ...node,
        status: 'processing' as const
      };
      
      // API呼び出し
      const response = await fetch('/api/extract/node', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nodeId: node.id,
          extractionPrompt: node.extractionPrompt,
          transcript
        })
      });
      
      // レスポンスを解析
      const data = await response.json();
      
      if (!response.ok) {
        // APIエラーの場合
        const errorInfo: ErrorInfo = {
          message: data.error || '抽出処理中にエラーが発生しました',
          code: data.code || 'API_ERROR'
        };
        
        return {
          ...node,
          status: 'error' as const,
          error: errorInfo,
          active: false
        };
      }
      
      // 成功: 抽出内容あり
      if (data.status === 'success' && data.contents && data.contents.length > 0) {
        return {
          ...node,
          contents: data.contents,
          content: data.contents[0]?.content || '', // 後方互換性のため
          importance: data.importance || node.importance,
          status: 'success' as const,
          error: null,
          active: true // 内容があるのでアクティブ
        };
      }
      
      // 部分的成功: 抽出内容なし
      return {
        ...node,
        contents: [],
        content: '',
        status: 'partial' as const,
        error: data.error || {
          message: '内容が抽出できませんでした',
          code: 'EMPTY_CONTENT'
        },
        active: false // 内容がないので非アクティブ
      };
      
    } catch (error) {
      // 通信エラーなど
      const errorInfo: ErrorInfo = {
        message: error instanceof Error ? error.message : '抽出処理中にエラーが発生しました',
        code: 'EXTRACTION_ERROR'
      };
      
      return {
        ...node,
        status: 'error' as const,
        error: errorInfo,
        active: false
      };
    } finally {
      setCurrentlyProcessing(null);
    }
  }, []);

  /**
   * エッジの関連性を抽出
   * @param edge 抽出対象のエッジ
   * @param sourceNodeActive ソースノードのアクティブ状態
   * @param targetNodeActive ターゲットノードのアクティブ状態
   * @param transcript トランスクリプト
   * @returns 更新されたエッジ
   */
  const extractEdgeContent = useCallback(async (
    edge: TopicEdge,
    sourceNodeActive: boolean,
    targetNodeActive: boolean,
    transcript: string
  ): Promise<TopicEdge> => {
    try {
      setCurrentlyProcessing(edge.id);
      
      // エッジのステータスを処理中に設定
      const processingEdge = {
        ...edge,
        status: 'processing' as const
      };
      
      // 両端のノードがアクティブでない場合は抽出をスキップ
      if (!sourceNodeActive || !targetNodeActive) {
        return {
          ...edge,
          contents: [],
          content: '',
          status: 'partial' as const,
          error: {
            message: '接続されたノードが非アクティブのため抽出をスキップします',
            code: 'INACTIVE_NODES'
          },
          active: false
        };
      }
      
      // API呼び出し
      const response = await fetch('/api/extract/edge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          edgeId: edge.id,
          sourceId: edge.source,
          targetId: edge.target,
          sourceNodeActive,
          targetNodeActive,
          extractionPrompt: edge.extractionPrompt,
          transcript
        })
      });
      
      // レスポンスを解析
      const data = await response.json();
      
      if (!response.ok) {
        // APIエラーの場合
        const errorInfo: ErrorInfo = {
          message: data.error || '抽出処理中にエラーが発生しました',
          code: data.code || 'API_ERROR'
        };
        
        return {
          ...edge,
          status: 'error' as const,
          error: errorInfo,
          active: false
        };
      }
      
      // 成功: 抽出内容あり
      if (data.status === 'success' && data.contents && data.contents.length > 0) {
        return {
          ...edge,
          contents: data.contents,
          content: data.contents[0]?.content || '', // 後方互換性のため
          status: 'success' as const,
          error: null,
          active: true // 内容があるのでアクティブ
        };
      }
      
      // 部分的成功: 抽出内容なし
      return {
        ...edge,
        contents: [],
        content: '',
        status: 'partial' as const,
        error: data.error || {
          message: '関連性が抽出できませんでした',
          code: 'EMPTY_CONTENT'
        },
        active: false // 内容がないので非アクティブ
      };
      
    } catch (error) {
      // 通信エラーなど
      const errorInfo: ErrorInfo = {
        message: error instanceof Error ? error.message : '抽出処理中にエラーが発生しました',
        code: 'EXTRACTION_ERROR'
      };
      
      return {
        ...edge,
        status: 'error' as const,
        error: errorInfo,
        active: false
      };
    } finally {
      setCurrentlyProcessing(null);
    }
  }, []);
  
  /**
   * すべてのノードとエッジを処理
   * @param nodes 処理対象のノード配列
   * @param edges 処理対象のエッジ配列
   * @param transcript トランスクリプト
   * @returns 更新されたノードとエッジ
   */
  const processAll = useCallback(async (
    nodes: TopicNode[],
    edges: TopicEdge[],
    transcript: string
  ): Promise<{ nodes: TopicNode[], edges: TopicEdge[] }> => {
    setExtracting(true);
    setProgress(0);
    
    try {
      // ノードのステータスを処理中に設定
      const processingNodes = nodes.map(node => ({
        ...node,
        status: 'processing' as const
      }));
      
      // エッジのステータスを処理中に設定
      const processingEdges = edges.map(edge => ({
        ...edge,
        status: 'processing' as const
      }));
      
      // ノードを処理
      const nodePromises = processingNodes.map((node, index) => 
        extractNodeContent(node, transcript).then(result => {
          // 進捗を更新
          setProgress(prev => prev + (50 / processingNodes.length));
          return result;
        })
      );
      
      // ノード処理が完了するまで待機
      const processedNodes = await Promise.all(nodePromises);
      
      // アクティブなノードのマップを作成
      const activeNodesMap = new Map<string, boolean>();
      processedNodes.forEach(node => {
        activeNodesMap.set(node.id, node.active);
      });
      
      // エッジを処理（ソースとターゲットのノードのアクティブ状態に基づく）
      const edgePromises = processingEdges.map((edge, index) => {
        const sourceNodeActive = activeNodesMap.get(edge.source) || false;
        const targetNodeActive = activeNodesMap.get(edge.target) || false;
        
        return extractEdgeContent(edge, sourceNodeActive, targetNodeActive, transcript)
          .then(result => {
            // 進捗を更新
            setProgress(prev => prev + (50 / processingEdges.length));
            return result;
          });
      });
      
      // エッジ処理が完了するまで待機
      const processedEdges = await Promise.all(edgePromises);
      
      setProgress(100);
      
      return { nodes: processedNodes, edges: processedEdges };
    } catch (error) {
      console.error('Process all error:', error);
      toast.error('抽出処理中にエラーが発生しました', {
        description: error instanceof Error ? error.message : '不明なエラー'
      });
      throw error;
    } finally {
      setExtracting(false);
    }
  }, [extractNodeContent, extractEdgeContent]);
  
  /**
   * ノード抽出を再試行
   * @param nodeId 再試行するノードのID
   * @param nodes ノード配列
   * @param transcript トランスクリプト
   * @returns 更新されたノード
   */
  const retryNode = useCallback(async (
    nodeId: string,
    nodes: TopicNode[],
    transcript: string
  ): Promise<TopicNode | null> => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return null;
    
    const updatedNode = {
      ...node,
      status: 'processing' as const
    };
    
    toast.info(`「${node.title}」の抽出を再試行しています...`);
    
    return extractNodeContent(updatedNode, transcript);
  }, [extractNodeContent]);
  
  /**
   * エッジ抽出を再試行
   * @param edgeId 再試行するエッジのID
   * @param nodes ノード配列
   * @param edges エッジ配列
   * @param transcript トランスクリプト
   * @returns 更新されたエッジ
   */
  const retryEdge = useCallback(async (
    edgeId: string,
    nodes: TopicNode[],
    edges: TopicEdge[],
    transcript: string
  ): Promise<TopicEdge | null> => {
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) return null;
    
    // ソースとターゲットのノードのアクティブ状態を取得
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return null;
    
    const updatedEdge = {
      ...edge,
      status: 'processing' as const
    };
    
    toast.info(`「${edge.question}」の抽出を再試行しています...`);
    
    return extractEdgeContent(updatedEdge, sourceNode.active, targetNode.active, transcript);
  }, [extractEdgeContent]);
  
  return {
    extracting,
    progress,
    currentlyProcessing,
    processAll,
    extractNodeContent,
    extractEdgeContent,
    retryNode,
    retryEdge
  };
}